import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../store';
import { getLoadingAllDocuments, areDocumentCountsAvailable } from '../store/apply/selectors';
import { getAllDocumentsWithCounts } from '../store/apply/actions';

const ALL_DOC_TYPES = [
  'passport',
  'photoId',
  'academicTranscript',
  'cv',
  'englishTest',
  'referenceLetter',
  'personalStatement',
  'researchProposal',
];

/**
 * Hook to preload all document types for an applicant.
 * This ensures document counts and data are available immediately when needed,
 * preventing duplicate API calls across different components.
 */
export function useApplicantDocuments(): { loading: boolean; loaded: boolean } {
  const params = useParams();
  const dispatch = useDispatch();

  const loading = useSelector(getLoadingAllDocuments);
  const countsAvailable = useSelector((state: ApplicationState) => areDocumentCountsAvailable(state, ALL_DOC_TYPES));

  const application = useSelector((state: ApplicationState) => state.apply.application);
  const hasApplication = application && application.userId;

  useEffect(() => {
    const uni = params.uni as string;
    const applicantId = params.userId as string;

    if (uni && applicantId && hasApplication && application.userId === applicantId) {
      dispatch(getAllDocumentsWithCounts(uni, applicantId, ALL_DOC_TYPES));
    }
  }, [params.uni, params.userId, dispatch, hasApplication, application?.userId]);

  return {
    loading,
    loaded: countsAvailable,
  };
}

/**
 * Hook to get document counts for specific types, with automatic fallback to fetch if needed.
 * This is the optimized version of useApplicantDocFileCount that leverages the preloaded data.
 */
export function useDocumentCounts(docTypes: string[]): number {
  // Memoize the docTypes array to prevent unnecessary re-renders
  const memoizedDocTypes = useMemo(() => docTypes, [docTypes.join(',')]);

  const count = useSelector((state: ApplicationState) => {
    const documentCounts = state.apply.documentCounts || {};
    return memoizedDocTypes.reduce((total, docType) => {
      return total + (documentCounts[docType] || 0);
    }, 0);
  });

  // No longer dispatch here - rely on preloading from useApplicantDocuments
  // This prevents race conditions and duplicate requests

  return count;
}
